import { useState, useEffect, useCallback, useRef } from 'react';
import './GuidedTour.css';

/* How long a step waits for its target before giving up on it. Long enough for a
   lazily-loaded screen to paint, short enough that nobody sits looking at a dimmed
   page wondering what is being pointed at. */
const MISSING_TARGET_MS = 3000;

export default function GuidedTour({ steps, onComplete }) {
    const [currentStep, setCurrentStep] = useState(0);
    const [targetRect, setTargetRect] = useState(null);
    const [tooltipStyle, setTooltipStyle] = useState({});
    const [visible, setVisible] = useState(false);
    const [arrowPlacement, setArrowPlacement] = useState('top');
    const [arrowLeft, setArrowLeft] = useState(null);
    const tooltipRef = useRef(null);
    const hasScrolledRef = useRef(false);
    // Held in a ref so the give-up timer below calls the current handler rather than
    // whichever one was in scope when its step mounted.
    const onCompleteRef = useRef(onComplete);
    onCompleteRef.current = onComplete;

    // Clamp so a steps array that shrinks under us can never index past the end
    const safeStep = Math.min(currentStep, steps.length - 1);
    const step = steps[safeStep];

    useEffect(() => {
        if (!step) return;

        hasScrolledRef.current = false;
        let resizeObserver = null;
        let prevKey = '';

        const measure = () => {
            const el = document.querySelector(step.target);
            if (!el) return;

            if (!hasScrolledRef.current) {
                // Guarded because this runs inside an effect, and an effect that throws
                // takes the screen the tour is drawn over down with it — a tour is the
                // one thing on the desk that must never be able to break the desk.
                // `scrollIntoView` is absent in jsdom and on some older mobile engines.
                el.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
                hasScrolledRef.current = true;
            }

            const rect = el.getBoundingClientRect();
            const padding = 6;
            const key = `${rect.top},${rect.left},${rect.width},${rect.height}`;
            if (key === prevKey) return;
            prevKey = key;

            setTargetRect({
                top: rect.top - padding,
                left: rect.left - padding,
                width: rect.width + padding * 2,
                height: rect.height + padding * 2,
            });
        };

        measure();
        const interval = setInterval(measure, 150);

        /* An optimisation, not a requirement: the interval above already re-measures,
           so where `ResizeObserver` is missing the tour simply tracks a little later
           rather than not at all. Guarded because this is inside an effect, and an
           effect that throws takes the screen down with it. */
        const observeSize = (node) => {
            if (typeof ResizeObserver === 'undefined' || !node) return null;
            const observer = new ResizeObserver(measure);
            observer.observe(node);
            return observer;
        };

        resizeObserver = observeSize(document.querySelector(step.target));

        const mutationObserver = new MutationObserver(() => {
            const found = document.querySelector(step.target);
            if (found) {
                measure();
                if (!resizeObserver) resizeObserver = observeSize(found);
            }
        });
        mutationObserver.observe(document.body, { childList: true, subtree: true });

        window.addEventListener('resize', measure);
        window.addEventListener('scroll', measure, true);

        const showTimer = setTimeout(() => setVisible(true), 100);

        /* A step whose target never appears would otherwise leave the tour drawn as
           nothing at all — no spotlight, no tooltip, and no way to reach Next or Skip,
           so it would come back on the next visit for ever. A window is given for the
           element to arrive (a lazily-loaded screen, a rail row still rendering); past
           it the step is given up on rather than waited on, and a tour whose steps have
           all been given up on finishes so it can be recorded as seen. */
        const giveUp = setTimeout(() => {
            if (document.querySelector(step.target)) return;
            if (currentStep < steps.length - 1) setCurrentStep((s) => s + 1);
            else onCompleteRef.current();
        }, MISSING_TARGET_MS);

        return () => {
            clearInterval(interval);
            clearTimeout(showTimer);
            clearTimeout(giveUp);
            resizeObserver?.disconnect();
            mutationObserver.disconnect();
            window.removeEventListener('resize', measure);
            window.removeEventListener('scroll', measure, true);
        };
    }, [step, currentStep, steps.length]);

    useEffect(() => {
        if (!targetRect || !tooltipRef.current) return;

        const tooltip = tooltipRef.current;
        const tooltipWidth = tooltip.offsetWidth;
        const tooltipHeight = tooltip.offsetHeight;
        const gap = 12;

        let left;
        let top;
        const isTopPlacement = step.placement.startsWith('top');

        const spaceBelow = window.innerHeight - (targetRect.top + targetRect.height + gap);
        const spaceAbove = targetRect.top - gap;
        const placeAbove = isTopPlacement || spaceBelow < tooltipHeight + 20;

        /*
         * A target taller than the room above and below it — the workspace rail is
         * the whole height of the page — leaves no band to sit in, so the tooltip
         * was pinned to the top edge with its heading at y≈0 and its body over the
         * very rows it was describing (finding C4). Beside it is the one place left.
         */
        const noBand = spaceAbove < tooltipHeight + 20 && spaceBelow < tooltipHeight + 20;

        if (noBand) {
            const toTheRight = targetRect.left + targetRect.width + gap + tooltipWidth + 12
                <= window.innerWidth;
            left = toTheRight
                ? targetRect.left + targetRect.width + gap
                : targetRect.left - tooltipWidth - gap;
            top = targetRect.top + Math.min(24, targetRect.height / 4);
            setArrowPlacement('none');
        } else {
            if (placeAbove) {
                top = targetRect.top - tooltipHeight - gap;
                setArrowPlacement('bottom');
            } else {
                top = targetRect.top + targetRect.height + gap;
                setArrowPlacement('top');
            }

            const alignment = step.placement.replace('top', '').replace('bottom', '') || '';
            switch (alignment) {
                case '-start':
                    left = targetRect.left;
                    break;
                case '-end':
                    left = targetRect.left + targetRect.width - tooltipWidth;
                    break;
                default:
                    left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
            }
        }

        // Inside the viewport on every edge, not just the two it used to check.
        left = Math.max(12, Math.min(left, window.innerWidth - tooltipWidth - 12));
        top = Math.max(12, Math.min(top, window.innerHeight - tooltipHeight - 12));

        const targetCenter = targetRect.left + targetRect.width / 2;
        setArrowLeft(Math.max(16, Math.min(targetCenter - left, tooltipWidth - 16)));
        setTooltipStyle({ top, left });
    }, [targetRect, step, currentStep]);

    const handleNext = useCallback(() => {
        setVisible(false);
        setTimeout(() => {
            if (currentStep < steps.length - 1) {
                setCurrentStep((s) => s + 1);
            } else {
                onComplete();
            }
        }, 200);
    }, [currentStep, steps.length, onComplete]);

    const handleSkip = useCallback(() => {
        onComplete();
    }, [onComplete]);

    if (!targetRect) return null;

    return (
        <div className="guided-tour-overlay">
            <svg className="guided-tour-svg" onClick={handleSkip}>
                <defs>
                    <mask id="tour-spotlight">
                        <rect x="0" y="0" width="100%" height="100%" fill="white" />
                        <rect
                            x={targetRect.left}
                            y={targetRect.top}
                            width={targetRect.width}
                            height={targetRect.height}
                            rx="8"
                            fill="black"
                        />
                    </mask>
                </defs>
                <rect
                    x="0" y="0"
                    width="100%" height="100%"
                    fill="rgba(0,0,0,0.55)"
                    mask="url(#tour-spotlight)"
                    className={visible ? 'guided-tour-backdrop-visible' : 'guided-tour-backdrop-hidden'}
                />
            </svg>

            <div
                className="guided-tour-highlight"
                style={{
                    top: targetRect.top,
                    left: targetRect.left,
                    width: targetRect.width,
                    height: targetRect.height,
                }}
            />

            <div
                ref={tooltipRef}
                className={`guided-tour-tooltip ${visible ? 'visible' : ''}`}
                style={tooltipStyle}
            >
                {arrowPlacement === 'top' && arrowLeft !== null && (
                    <div
                        className="guided-tour-arrow guided-tour-arrow-top"
                        style={{ left: arrowLeft }}
                    />
                )}
                {arrowPlacement === 'bottom' && arrowLeft !== null && (
                    <div
                        className="guided-tour-arrow guided-tour-arrow-bottom"
                        style={{ left: arrowLeft }}
                    />
                )}

                <div className="guided-tour-content">
                    <div className="guided-tour-header">
                        <span className="guided-tour-step-label">
                            Step {safeStep + 1} of {steps.length}
                        </span>
                        <button onClick={handleSkip} className="guided-tour-skip-btn">
                            Skip tour
                        </button>
                    </div>

                    <h3 className="guided-tour-title">{step.title}</h3>
                    <p className="guided-tour-description">{step.description}</p>

                    <div className="guided-tour-footer">
                        <div className="guided-tour-dots">
                            {steps.map((_, i) => (
                                <div
                                    key={i}
                                    className={`guided-tour-dot ${
                                        i === safeStep
                                            ? 'active'
                                            : i < safeStep
                                                ? 'completed'
                                                : ''
                                    }`}
                                />
                            ))}
                        </div>

                        <button onClick={handleNext} className="guided-tour-next-btn">
                            {safeStep < steps.length - 1 ? 'Next' : 'Finish'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
